<template>
  <div role="group" :class="[bem('', { disabled }), attrs.class]" :style="attrs.style">
    <button
      v-if="!hideBtn"
      type="button"
      :class="[bem('btn', { disabled: minusDisabled })]"
      :disabled="minusDisabled"
      @click="handleMinus"
    >
      <MIcon prefix="g" icon="minus" :size="24" class="leading-none" />
    </button>

    <input
      ref="inputRef"
      :class="[bem('input'), hideBtn ? 'px-8px' : 'text-center']"
      :value="currentValue"
      :disabled="disabled"
      :readonly="inMtsApp"
      inputmode="decimal"
      autocomplete="off"
      :aria-valuemax="max"
      :aria-valuemin="min"
      :aria-valuenow="currentValue"
      :placeholder="t(placeholder)"
      @input="(e) => handleInput((e.target as HTMLInputElement).value)"
      @focus="handleFocus"
      @blur="handleBlur"
    />

    <span v-if="$slots.suffix || suffix" :class="bem('suffix')">
      <slot name="suffix">{{ suffix }}</slot>
    </span>

    <button
      v-if="!hideBtn"
      type="button"
      :class="[bem('btn', { disabled: plusDisabled })]"
      :disabled="plusDisabled"
      @click="handlePlus"
    >
      <MIcon prefix="g" icon="plus" :size="24" class="leading-none" />
    </button>
  </div>
</template>

<script setup lang="ts">
  import Big from 'big.js';
  import { clamp } from 'lodash-es';
  import { computed, HTMLAttributes, ref, useAttrs, useTemplateRef, watch } from 'vue';
  import type { Numeric } from '@mts-fe/types';
  import { useKeyboard, VirtualKeyboardProps } from '@mts-fe/hooks';
  import { createNamespace, formatNumber, isDef } from '@mts-fe/utils';
  import { t } from '@mts/hooks/web/useI18n';
  import { inMtsApp } from '@mts/utils';

  defineOptions({ name: 'Stepper', inheritAttrs: false });

  const [_, bem] = createNamespace('stepper');

  interface StepperProps {
    value?: Numeric;
    min?: Numeric;
    max?: Numeric;
    step?: number;
    precision?: number;
    disabled?: boolean;
    placeholder?: string;
    hideBtn?: boolean;
    suffix?: string;
    /** 虚拟键盘配置项 */
    config?: VirtualKeyboardProps & { disabled?: boolean };
  }

  const props = withDefaults(defineProps<StepperProps>(), {
    max: Infinity,
    min: 0,
    step: 1,
    disabled: false,
    placeholder: '',
    config: () => ({ symbol: '.' }),
  });
  const attrs = useAttrs() as HTMLAttributes;

  const emit = defineEmits<{
    (e: 'update:value', value: Numeric): void;
    (e: 'change', value: Numeric): void;
    (e: 'focus', value: FocusEvent): void;
    (e: 'blur', value?: FocusEvent): void;
  }>();

  const isEqual = (value1: unknown, value2: unknown): boolean => String(value1) === String(value2);

  const format = (value?: Numeric): Numeric => {
    if (props.hideBtn) return value ?? '';

    const min = +props.min;
    if (value === '' || value === void 0) return min;

    const max = +props.max;
    value = formatNumber(String(value));
    value = value === '' ? 0 : +value;
    value = Number.isNaN(value) ? min : clamp(+value, min, max);

    return isDef(props.precision) ? +value.toFixed(props.precision) : value;
  };

  const getInitialValue = () => {
    const value = format(props.value);

    if (!isEqual(value, props.value)) {
      emit('update:value', value);
    }
    return value;
  };

  const inputRef = useTemplateRef('inputRef');
  const currentValue = ref<Numeric>(getInitialValue());
  watch(
    currentValue,
    (newVal) => {
      if (newVal === props.value) return;
      emit('update:value', newVal);
      emit('change', newVal);
    },
    { immediate: true },
  );

  watch(
    () => props.value,
    (value) => {
      if (!isEqual(value, currentValue.value)) {
        const newVal = format(value);
        if (currentValue.value === newVal) {
          emit('update:value', newVal);
          emit('change', newVal);
        } else {
          currentValue.value = newVal;
        }
      }
    },
  );

  const minusDisabled = computed(() => props.disabled || +currentValue.value <= +props.min);
  const plusDisabled = computed(() => props.disabled || +currentValue.value >= +props.max);

  const changeValueByStep = (method: 'minus' | 'plus') => {
    const formattedValue = format(Big(currentValue.value)[method](props.step).toNumber());

    if (!isEqual(formattedValue, currentValue.value)) {
      currentValue.value = formattedValue;
    }
  };

  const handleMinus = () => !minusDisabled.value && changeValueByStep('minus');
  const handlePlus = () => !plusDisabled.value && changeValueByStep('plus');

  const handleInput = (value: string) => {
    let formatted = formatNumber(value);
    if (!isEqual(value, formatted)) {
      (inputRef.value as HTMLInputElement).value = formatted;
    }

    currentValue.value = formatted === String(+formatted) ? +formatted : formatted;
  };

  const handleFocus = (event: FocusEvent) => {
    (event.target as HTMLInputElement).select();
    emit('focus', event);

    const { disabled, ...keyboardConfig } = props.config;
    if (disabled) return;

    useKeyboard(inputRef.value, keyboardConfig, {
      onInput: handleInput,
      onDelete: handleInput,
      onClose: () => inputRef.value?.blur(),
    });
  };

  const handleBlur = (event: FocusEvent) => {
    if (
      !props.hideBtn &&
      (currentValue.value === undefined || currentValue.value.toString().trim() === '')
    ) {
      currentValue.value = +props.min;
    } else {
      currentValue.value = format(currentValue.value);
    }

    const input = inputRef.value as HTMLInputElement;
    if (!input) return;
    if (input.value !== currentValue.value.toString()) {
      input.value = currentValue.value.toString();
    }
    emit('blur', event);
  };
</script>

<style lang="less">
  .mts-stepper {
    display: inline-flex;
    position: relative;
    align-items: center;
    width: 100%;
    min-width: 0;
    padding: 7px 0;
    overflow: hidden;
    transition: all 0.2s;
    border: 1px solid #d5d9e1;
    border-radius: @border-radius;
    background-color: #fff;

    &:focus-within {
      z-index: 1;
      border-color: #1fb592;
      outline: 0;
      box-shadow: 0 0 0 2px rgb(13 134 92 / 19%);
    }

    &--disabled {
      border-color: #ebedf0;
      background-color: #ebedf0;
    }

    &__btn {
      display: flex;
      padding: 0 8px;
      border: none;
      border-radius: 0;
      background-color: transparent;
      color: #01a887;
      cursor: pointer;

      &--disabled {
        color: #c0c6d0;
        cursor: not-allowed;
      }
    }

    &__input {
      width: 100%;
      border: none;
      outline: none;
      background-color: transparent;
      font-size: inherit;
    }

    &__suffix {
      display: inline-flex;
      flex-shrink: 0;
      align-items: center;
      padding-right: 8px;
      color: #8c8c8c;
      font-size: inherit;
      white-space: nowrap;
    }
  }
</style>
